<template>
  <footer class="AppFooter">
    <div class="AppFooter-container PageContainer">
      <div class="AppFooter-column1">
        <PoweredBy :label="t('ui.global.poweredBy')" class="AppFooter-disclaimer" />

        <div v-if="te('custom.layout.footer.copyright')" class="AppFooter-copyright">
          {{ copyright }}
        </div>
      </div>

      <div class="AppFooter-column2">
        <div class="AppFooter-links">
          <NuxtLink v-if="faqsLabel" :to="faqsLink" class="AppFooter-link">
            <ActionButton tag="span" size="sm" variant="tertiary">
              {{ faqsLabel }}
            </ActionButton>
          </NuxtLink>

          <NuxtLink
            :to="t('custom.meta.privacyPolicy.link')"
            target="_blank"
            rel="noopener noreferrer"
            class="AppFooter-link"
          >
            <ActionButton tag="span" size="sm" variant="tertiary">
              {{ t('custom.meta.privacyPolicy.label') }}
            </ActionButton>
          </NuxtLink>

          <NuxtLink
            :to="t('custom.meta.termsAndConditions.link')"
            target="_blank"
            rel="noopener noreferrer"
            class="AppFooter-link"
          >
            <ActionButton tag="span" size="sm" variant="tertiary">
              {{ t('custom.meta.termsAndConditions.label') }}
            </ActionButton>
          </NuxtLink>

          <NuxtLink
            :to="t('custom.meta.website.link')"
            target="_blank"
            rel="noopener noreferrer"
            class="AppFooter-link"
          >
            <ActionButton tag="span" size="sm" variant="tertiary">
              {{ t('custom.meta.website.label') }}
            </ActionButton>
          </NuxtLink>
        </div>

        <div v-if="showContact" class="AppFooter-contact">
          {{ t('custom.layout.footer.contact') }}
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">

withDefaults(defineProps<{
  showContact?: boolean
}>(), {
  showContact: true,
});

const { t, te, tf } = useI18n();
const { faqsLink } = useFaqsLink();

const currentYear = new Date().getFullYear();
const copyright = computed(() => t('custom.layout.footer.copyright', { year: currentYear }));

// The label gates the link: it ships from the translations repo, so the entry stays out until it lands.
const faqsLabel = computed(() => tf('ui.layout.footer.faqs'));
</script>

<style lang="scss">
@use 'assets/scss/theme' as vars;
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-base {
  .AppFooter {
    position: relative;
    width: 100%;

    background-color: vars.$molecules-footer-bg-color;
    border-top: vars.$molecules-footer-border-stroke solid vars.$molecules-footer-border-color;
    color: vars.$molecules-footer-secondary-text-color;

    &-container {
      display: flex;
      justify-content: space-between;
      min-height: vars.$dimensions-d-20;
    }

    &-column1 {
      position: relative;
      flex-shrink: 0;
      align-self: stretch;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-between;
      height: 100%;
      min-height: vars.$dimensions-d-16;
      box-sizing: border-box;
      padding: 0;
    }

    &-disclaimer {
      color: vars.$molecules-footer-disclaimer-color;
    }

    &-copyright {
      position: relative;
      flex-shrink: 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: start;
      gap: 10px;
      box-sizing: border-box;
      padding: 0;
      font-weight: vars.$typography-weight-body;
      font-size: vars.$font-size-sm;
      text-align: end;
      letter-spacing: -0.42px;
      color: vars.$molecules-footer-disclaimer-color;
    }

    &-column2 {
      position: relative;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: space-between;
      gap: vars.$dimensions-d-5;
      min-height: vars.$dimensions-d-16;
      box-sizing: border-box;
      padding: 0;
    }

    &-links {
      position: relative;
      flex-shrink: 0;
      flex-wrap: wrap;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: start;
    }

    &-link {
      max-width: 360px;

      @include responsive.tablet {
        &:not(:last-child) {
          border-inline-end: 1px solid vars.$molecules-footer-border-color;
          padding-inline-end: vars.$dimensions-d-1;
          margin-inline-end: vars.$dimensions-d-1;
        }
      }

      &:hover {
        color: vars.$color-system-status-info-900;
      }
    }

    &-contact {
      position: relative;
      flex-shrink: 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: start;
      gap: 10px;
      padding: 0;
      font-weight: vars.$typography-weight-body;
      font-size: vars.$font-size-sm;
      text-align: end;
      letter-spacing: -0.42px;
    }

    // Responsive behavior
    @include responsive.mobile {
      &-container {
        flex-direction: column;
        gap: vars.$dimensions-d-5;
      }

      &-links {
        flex-direction: column;
      }

      &-column1,
      &-column2 {
        align-items: center;
        width: 100%;
      }

      &-copyright,
      &-contact {
        text-align: center;
      }
    }
  }
}
</style>
